import { LitElement, html, unsafeCSS, render } from "lit";
import { property, query, state } from "lit/decorators.js";
import type { InternalSupportItem } from "./support-menu/types/SupportItem.js";
import {
  CAPABILITY_FLAG_NAMES,
  type CapabilityFlags,
} from "./support-menu/types/CapabilityFlags.js";
import { buildFilteredItems } from "./support-menu/utils/build-filtered-items.js";
import { normalizeLocale } from "./support-menu/utils/normalize-locale.js";
import {
  SUPPORTED_LOCALES,
  SUPPORT_BUTTON_LABEL_KEY,
  lookupLabel,
  type SupportedLocale,
} from "./support-menu/i18n/index.js";
import {
  injectPendoOverride,
  removePendoOverride,
  incrementDarkThemeHook,
  decrementDarkThemeHook,
} from "./support-menu/utils/pendo-override.js";
import { PANEL_ID } from "./support-menu/components/support-menu-trigger/SupportMenuTrigger.js";
import "./support-menu/components/support-menu-trigger/SupportMenuTrigger.js";
import "./support-menu/components/support-menu-panel/SupportMenuPanel.js";
import "./support-menu/components/support-menu-item/SupportMenuItem.js";
import tailwindShared from "./styles/tailwind-shared.css?inline";
import styles from "./App.css?inline";

const TAG = "enterprise-product-experience-support-menu";
const HELP_BUTTON_HOST_ID = "bridge-support-menu-help-button";

// Custom boolean-attribute converter. Lit's default `type: Boolean` treats any
// non-null attribute value (including the literal string `"false"`) as true,
// which is wrong for hosts that emit attributes from JSON config. Contract:
//   - attribute absent / removed (null)       → undefined  (field unset)
//   - presence-only `<el showlms>` (empty "") → true       (HTML idiom)
//   - "true" (case-insensitive, trimmed)      → true       (tolerates "  TRUE ")
//   - anything else                           → false      (fail-closed)
// JSON-config hosts (Bridge Bar LD variations) MUST emit "true" / "false" —
// never "" or null. Empty string is the HTML presence idiom and means true.
const fromBooleanAttribute = (v: string | null): boolean | undefined => {
  if (v === null) return undefined;
  if (v === "") return true;
  return v.trim().toLowerCase() === "true";
};

// showPendo mode-toggle converter — DELIBERATE INVERTED DEFAULT (vs. fromBooleanAttribute above).
// While show* capability flags fail *closed* (absent → undefined/false → panel hidden),
// showPendo fails *to Pendo* (absent → true) because:
//   - AC-F7: if the host cannot supply the attribute, Pendo is the safe default.
//   - Product steady-state: Pendo mode is the expected production posture.
// Contract (pair with fromBooleanAttribute; divergence is intentional):
//   - attribute absent / removed (null)              → true   (Pendo — INVERTED from show* flags)
//   - presence-only `<el showpendo>` (empty "")      → true   (Pendo)
//   - "true" (case-insensitive, trimmed)             → true   (Pendo)
//   - "false" (case-insensitive, trimmed)            → false  (custom slideout)
//   - anything else (garbage / unknown)              → true   (fail-to-Pendo)
// ⚠ Do NOT replace with fromBooleanAttribute for "consistency" — that would
// invert the absent-case to off (custom slideout) and silently break AC-F7.
const fromPendoAttribute = (v: string | null): boolean =>
  v === null ? true : v.trim().toLowerCase() !== "false";

export class SupportMenuElement extends LitElement {
  static readonly styles = [unsafeCSS(tailwindShared), unsafeCSS(styles)];

  // ── individual capability attributes (host-emitted; one boolean per flag) ──
  // Public API: optional boolean HTML attributes parsed by `fromBooleanAttribute`
  // (defined above). The field stays undefined until the host passes the
  // attribute; `"true"` / presence-only → true, `"false"` / unrecognized → false.
  // The strict `=== true` gate in buildFilteredItems still treats undefined and
  // false identically (both fail-close), so visibility is correct in every state.
  // `declare` is correct ONLY because tsconfig has `useDefineForClassFields: false`
  // — Lit installs accessors on the prototype that would be shadowed by class
  // fields under the TS5/ES2022+ default. If that flag flips, switch to a
  // definite-assignment assertion or restore explicit `= undefined` initializers.
  @property({
    attribute: "showlms",
    converter: { fromAttribute: fromBooleanAttribute },
  })
  declare showLms?: boolean;

  // MODE toggle: host emits showpendo="true"|"false" (server-side LD result).
  // Default true = Pendo mode (AC-F7 fail-safe: absent → Pendo).
  // NOT a panel capability — kept out of CAPABILITY_FLAG_NAMES/CAPABILITY_REGISTRY.
  @property({
    attribute: "showpendo",
    converter: { fromAttribute: fromPendoAttribute },
  })
  showPendo = true;

  // ── bb_* passive pass-throughs (ADR-001 §6 — no auth in component) ────────
  @property({ attribute: "bb_session" }) bbSession = "";
  @property({ attribute: "bb_user" }) bbUser = "";
  @property({ attribute: "bb_lang" }) bbLang = "";
  // bb_theme is the ONE bb_* attribute the component reads and acts on (the rest
  // stay passive pass-throughs). It is theme metadata only — never parsed for
  // identity/auth (ADR-001 intact). It drives TWO independent dark surfaces:
  //   1. the Get Help button — via the trigger's `dark` property, in BOTH Pendo and
  //      legacy mode (the button always renders, so it follows the page theme); and
  //   2. the Pendo container — via the ref-counted document hook, gated on Pendo
  //      injection (there is no container to theme in legacy mode).
  // Recorded as EXCEPTION (US2003211) in project-context.md + architecture.md.
  // Default "" → light.
  @property({ attribute: "bb_theme" }) bbTheme = "";
  @property({ attribute: "bb_env" }) bbEnv = "";

  // P6: lowercase via converter so attribute and event transports are consistent.
  // bb_product_context no longer feeds a capability filter (L2 removed under
  // SCP-2026-05-20-us1959652-flag-bag); it now only flows into analytics.
  @property({
    attribute: "bb_product_context",
    converter: {
      fromAttribute: (v: string | null) => (v ?? "unknown").toLowerCase(),
    },
  })
  bbProductContext = "unknown";

  // ── internal state ────────────────────────────────────────────────────────
  @state() private _panelOpen = false;
  // Initialize via buildFilteredItems({}, this.bbEnv) so the no-attrs case
  // yields [help-center, support-code]-with-live-fallback at construction.
  // Without this, optional show* properties with no initializer never enter
  // the first updated()'s `changed` Map, and AC#5 (Help Center always visible)
  // would break on first render. At construction `this.bbEnv === ""`, so the
  // resolver returns the live URL; the updated() recompute below re-resolves
  // once the `bb_env` attribute lands (mirrors the `bbProductContext` handling).
  @state() private _filteredItems: InternalSupportItem[] = buildFilteredItems(
    {},
    this.bbEnv,
  );
  @state() private _productContext = "unknown";
  // US2017758: the active locale resolved from bb_lang (normalize → recognize →
  // fallback "en"). Drives BOTH the trigger's .locale binding and the cached label.
  @state() private _resolvedLocale: SupportedLocale = "en";
  // US2017758 (ARGUS PR #153 [Medium]): the localized label cached alongside
  // _resolvedLocale. Resolved ONCE in _resolveLocale() — the only place the locale
  // changes — rather than per render(). render() re-runs on every reactive update
  // (_panelOpen toggle, bbTheme flip, _filteredItems recompute, showPendo flip), and
  // calling lookupLabel() there would re-enter i18next t() + a second normalizeLocale
  // pass on each one even though the label only depends on _resolvedLocale. Caching it
  // makes the label a pure function of the single input that drives it.
  @state() private _resolvedLabel: string = lookupLabel(
    SUPPORT_BUTTON_LABEL_KEY,
    "en",
  );

  // P12: guard against double listener registration on DOM re-insertion
  private _listenerRegistered = false;
  // Per-instance guard so this instance counts at most once against the shared
  // pendo-override ref-count. Set when connectedCallback injects; cleared on
  // disconnect. Because injection is driven from connectedCallback (which fires
  // on every (re)connection) rather than firstUpdated (once per element
  // lifetime), the shared <style> is restored on DOM re-attach — mirrors the
  // _listenerRegistered re-registration pattern below.
  private _pendoInjected = false;
  // US2003211 (PR #109 review): tracks whether THIS instance currently holds one unit
  // of the shared dark-hook ref-count. Keeps the instance balanced (exactly one
  // increment outstanding) across connect / bb_theme flips / DOM re-attach so it never
  // double-counts or leaks a unit. Driven only through _syncDarkThemeHook().
  private _darkHooked = false;
  // US2017758 (AD-4): once-per-instance latch so an unrecognized bb_lang emits at
  // MOST ONE console.warn per element lifetime — even across repeated attribute
  // flips and DIFFERENT unrecognized values. Deliberately NOT theme's
  // per-distinct-value _lastWarnedTheme compare; mirrors the _pendoInjected /
  // _listenerRegistered guard idiom.
  private _localeWarnEmitted = false;
  private _onContextChange!: (e: Event) => void;
  // P26: debounce timer for rapid context-change events
  private _contextDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  // P1: imperative panel reference — NOT rendered in Lit's shadow DOM template
  private _panelEl:
    | (HTMLElement & {
        items: InternalSupportItem[];
        productContext: string;
        open: boolean;
      })
    | null = null;

  // P9: stable reference to trigger for focus-return on panel close
  @query("support-menu-trigger") private _triggerEl!: HTMLElement;

  override connectedCallback(): void {
    super.connectedCallback();
    // Stable light-DOM id for Pendo agent targeting (AC-F1). Set AFTER super.connectedCallback()
    // (Lit requires super first) but BEFORE firstUpdated (Pendo may query the element immediately
    // on connection). Host-set id takes precedence; warn on duplicate — only one instance per
    // page is supported (see CONSUMER-GUIDE.md).
    if (!this.id) {
      const existing = document.getElementById(HELP_BUTTON_HOST_ID);
      if (existing && existing !== this && existing.isConnected) {
        console.warn(
          "[enterprise-product-experience-support-menu] Multiple instances detected. " +
            "Only one instance per page is supported; set a unique `id` attribute on " +
            "each instance to suppress this warning. See CONSUMER-GUIDE.md.",
        );
      } else {
        this.id = HELP_BUTTON_HOST_ID;
      }
    }

    // Pendo mode: push pendo_override.css into document.head so its rules reach
    // document.body / Pendo UI outside our shadow root (AC-2). Driven from here
    // (not firstUpdated) and guarded by _pendoInjected so the shared <style> is
    // re-established on DOM re-attach — connectedCallback fires on every
    // (re)connection, firstUpdated only once. The post-mount showPendo flip
    // stays panel-only (documented mount-time-mode deferral; updated()).
    if (this.showPendo && !this._pendoInjected) {
      injectPendoOverride();
      this._pendoInjected = true;
    }

    // US2003211 AC7: register this instance's dark contribution for the just-injected
    // CSS. _syncDarkThemeHook is gated on _pendoInjected so a legacy-mode element never
    // reflects a dark hook onto <html> for CSS that isn't there. Runtime bb_theme flips
    // are handled in updated(); disconnect releases the contribution.
    this._syncDarkThemeHook();
    // Mode-independent: surface a bb_theme typo even in legacy mode (no theming there,
    // but the misconfiguration is still worth flagging). Deduped, so the connect call +
    // the first updated() call below do not double-warn.
    this._warnIfUnrecognizedTheme();

    // US2017758: resolve the active locale from bb_lang BEFORE first render so the
    // initial label/aria-label render in the resolved language (no English flash).
    // Runtime bb_lang changes are handled in updated(); the warn is deduped so the
    // connect call + the first updated() call do not double-warn.
    this._resolveLocale();

    // Legacy (custom slideout) mode builds its panel here — NOT in firstUpdated —
    // so it survives DOM re-attach (firstUpdated fires once per element lifetime;
    // connectedCallback fires on every (re)connection, just like the listener and
    // Pendo-CSS restoration above). _createPanel() early-returns when this._panelEl
    // is set, so calling it on every reconnect is idempotent.
    if (!this.showPendo) this._createPanel();

    if (this._listenerRegistered) return;

    this._onContextChange = (e: Event) => {
      const detail = (e as CustomEvent<{ productContext?: string }>).detail;
      if (typeof detail !== "object" || detail === null) return;
      if (this._contextDebounceTimer !== null)
        clearTimeout(this._contextDebounceTimer);
      this._contextDebounceTimer = setTimeout(() => {
        this._productContext = (
          detail?.productContext ?? "unknown"
        ).toLowerCase();
        this._contextDebounceTimer = null;
      }, 30);
    };

    window.addEventListener(
      "bridge:product-context-change",
      this._onContextChange,
    );
    this._listenerRegistered = true;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(
      "bridge:product-context-change",
      this._onContextChange,
    );
    this._listenerRegistered = false;
    if (this._contextDebounceTimer !== null) {
      clearTimeout(this._contextDebounceTimer);
      this._contextDebounceTimer = null;
    }
    this._panelEl?.remove();
    this._panelEl = null;
    // AC-4: drop this instance's hold on the shared pendo-override <style>. The
    // ref-count removes the node only when the last Pendo-mode instance unmounts.
    if (this._pendoInjected) {
      removePendoOverride();
      this._pendoInjected = false;
    }
    // US2003211 (PR #109 review): release this instance's dark-hook contribution.
    // Now that _pendoInjected is false, _syncDarkThemeHook decrements the shared
    // ref-count iff this instance was holding a unit — so a light sibling unmounting
    // never strips a still-mounted dark instance's hook.
    this._syncDarkThemeHook();
  }

  override firstUpdated(): void {
    // Intentionally empty. Both the legacy-mode panel and the Pendo-mode CSS
    // injection now live in connectedCallback (re-attach safe). firstUpdated
    // fires only once per element lifetime, so any lifecycle state put here would
    // be lost on DOM re-attach — the bug this symmetry is written to prevent.
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("bbProductContext")) {
      this._productContext = this.bbProductContext;
    }

    // Data-driven dispatch: any capability flag in CAPABILITY_FLAG_NAMES
    // triggers a re-filter, and the bag is built from the same list. Adding
    // a new flag is a single edit to CAPABILITY_FLAG_NAMES — there is no
    // hand-maintained `changed.has(…)` branch or bag literal to forget.
    // `changed.has("bbEnv")` is included so support-code's hrefByEnv re-resolves
    // when the host emits `bb_env` after construction (US1944211).
    if (
      CAPABILITY_FLAG_NAMES.some((name) => changed.has(name)) ||
      changed.has("bbEnv")
    ) {
      const flags: CapabilityFlags = {};
      for (const name of CAPABILITY_FLAG_NAMES) {
        flags[name] = this[name];
      }
      this._filteredItems = buildFilteredItems(flags, this.bbEnv);
    }

    if (changed.has("showPendo")) {
      if (this.showPendo) {
        if (this._panelOpen) this._panelOpen = false; // coalesced — same microtask, no extra render cycle
        this._panelEl?.remove();
        this._panelEl = null;
      } else {
        this._createPanel();
      }
      // DEFERRAL (documented mount-time-mode scope, US1990924 AC-F5): this flip
      // is panel-only by design — we do NOT call inject/removePendoOverride here.
      // Consequence accepted: a runtime true→false flip leaves the Pendo <style>
      // in document.head until disconnect, so its rules can still reach
      // document.body while the element is in legacy mode. Low risk today (CSS is
      // a no-op placeholder); revisit when real Pendo rules land (flag via
      // /bmad-correct-course, not a bug in this story).
    }

    // US2003211 AC7/AC9: a runtime bb_theme change re-drives this instance's dark
    // contribution. _syncDarkThemeHook is gated on _pendoInjected (legacy mode owns no
    // Pendo container to theme, so it never leaves a dark hook on <html>) and is
    // balanced via _darkHooked, so a no-op flip is harmless.
    if (changed.has("bbTheme")) {
      this._syncDarkThemeHook();
      this._warnIfUnrecognizedTheme();
    }

    // US2017758: a runtime bb_lang change re-resolves the active locale. Sibling of
    // the bbTheme branch above. Setting _resolvedLocale (@state) drives a re-render
    // so the trigger's .label (via lookupLabel) and .locale update.
    if (changed.has("bbLang")) {
      this._resolveLocale();
    }

    if (
      changed.has("_panelOpen") ||
      changed.has("_filteredItems") ||
      changed.has("_productContext")
    ) {
      this._syncPanel();
    }
  }

  // US2003211: the dark-mode signal is the literal bridge-bar contract value
  // "Interstate-Dark" (case-sensitive, as emitted by bridge-bar / the POC);
  // anything else (incl. "Interstate-Light" and the "" default) is light. Pure
  // predicate — the unrecognized-value diagnostic lives in _warnIfUnrecognizedTheme
  // so it fires regardless of Pendo/legacy mode and is not gated behind the
  // _syncDarkThemeHook short-circuit (PR #109 review round 2).
  private _isDarkTheme(): boolean {
    return this.bbTheme === "Interstate-Dark";
  }

  // Last bb_theme value already warned about — dedups the diagnostic so a single typo
  // does not spam on every render / re-attach / sibling instance (PR #109 review r2).
  private _lastWarnedTheme: string | null = null;

  // PR #109 review (r2): fail-visible at the attribute boundary, mode-independent and
  // deduped. The match stays case-sensitive per the bridge-bar contract, but any other
  // non-empty value (e.g. "interstate-dark", "dark") warns ONCE per distinct value so an
  // integration typo surfaces immediately instead of silently rendering light — in both
  // Pendo and legacy mode, and without the double-fire from connect + first updated().
  private _warnIfUnrecognizedTheme(): void {
    if (
      this.bbTheme !== "" &&
      this.bbTheme !== "Interstate-Dark" &&
      this.bbTheme !== "Interstate-Light" &&
      this.bbTheme !== this._lastWarnedTheme
    ) {
      this._lastWarnedTheme = this.bbTheme;
      console.warn(
        `[${TAG}] Unrecognized bb_theme: "${this.bbTheme}". ` +
          'Expected "Interstate-Dark" or "Interstate-Light".',
      );
    }
  }

  // US2017758: resolve bb_lang → _resolvedLocale. Empty / absent → "en" (no warn).
  // Recognized after normalization → that locale. Unrecognized non-empty → "en"
  // PLUS a single per-instance warn (AD-4). SUPPORTED_LOCALES (imported from the
  // i18n module) is the SOLE recognized-locale set — no second list lives here.
  // locale detection is bb_lang-only: no navigator.language / window / document
  // read anywhere in this path (Hard Rule #4).
  private _resolveLocale(): void {
    const normalized = normalizeLocale(this.bbLang);
    if (normalized === "") {
      this._resolvedLocale = "en";
    } else if ((SUPPORTED_LOCALES as readonly string[]).includes(normalized)) {
      this._resolvedLocale = normalized as SupportedLocale;
    } else {
      this._resolvedLocale = "en";
      this._warnIfUnrecognizedLocale();
    }
    // ARGUS PR #153 [Medium]: resolve the label HERE — the single place the locale
    // changes — so it stays out of the hot render() path. _resolvedLocale is already
    // canonical (∈ SUPPORTED_LOCALES), so lookupLabel's own normalize is a guaranteed
    // no-op (see the ownership note in i18n.ts).
    this._resolvedLabel = lookupLabel(
      SUPPORT_BUTTON_LABEL_KEY,
      this._resolvedLocale,
    );
  }

  // US2017758 (AD-4): warn AT MOST ONCE per instance for an unrecognized bb_lang —
  // even across repeated attribute flips and DIFFERENT unrecognized values — via
  // the once-per-instance boolean latch _localeWarnEmitted (deliberately NOT
  // theme's per-distinct-value _lastWarnedTheme compare).
  private _warnIfUnrecognizedLocale(): void {
    if (this._localeWarnEmitted) return;
    this._localeWarnEmitted = true;
    console.warn(
      `[${TAG}] Unrecognized bb_lang: "${this.bbLang}". ` +
        `Falling back to "en". Supported: ${SUPPORTED_LOCALES.join(", ")}.`,
    );
  }

  // US2003211 (PR #109 review): drive this instance's single contribution to the
  // shared dark-hook ref-count (pendo-override.ts). Increments once when the instance
  // wants dark (Pendo CSS injected + bb_theme="Interstate-Dark") and isn't already
  // counted; decrements once when it stops wanting dark. _darkHooked keeps it balanced
  // across connect / bb_theme flips / DOM re-attach so it never double-counts or leaks.
  // The && short-circuit means _isDarkTheme() (and its warning) is skipped in legacy
  // mode and on teardown — exactly where theming doesn't apply.
  private _syncDarkThemeHook(): void {
    const wantsDark = this._pendoInjected && this._isDarkTheme();
    if (wantsDark && !this._darkHooked) {
      incrementDarkThemeHook();
      this._darkHooked = true;
    } else if (!wantsDark && this._darkHooked) {
      decrementDarkThemeHook();
      this._darkHooked = false;
    }
  }

  private _createPanel(): void {
    if (this._panelEl) return;
    const panel = document.createElement(
      "support-menu-panel",
    ) as HTMLElement & {
      items: InternalSupportItem[];
      productContext: string;
      open: boolean;
    };
    panel.setAttribute("id", PANEL_ID);
    panel.addEventListener("close-panel", () => this._handleClosePanel());
    document.body.appendChild(panel);
    this._panelEl = panel;
    this._syncPanel();
  }

  private _syncPanel(): void {
    if (!this._panelEl) return;
    // Defensive copy: panel is portalled into document.body, so its `items`
    // ref is reachable from host-page scripts. A shared array would let an
    // external mutation reorder _filteredItems without re-firing updated().
    this._panelEl.items = [...this._filteredItems];
    this._panelEl.productContext = this._productContext;
    this._panelEl.open = this._panelOpen;
    render(
      html`${this._filteredItems.map(
        (item) => html`<support-menu-item .item=${item}></support-menu-item>`,
      )}`,
      this._panelEl,
    );
  }

  private _handleTriggerToggle(): void {
    // Pendo mode: button is passive; Pendo's host-page agent handles the open.
    // Do NOT flip _panelOpen — no slideout, no transition (AC-F2, AC-F3).
    // Keyboard (Enter/Space) and mouse both route here, so AC-F6 is covered.
    if (this.showPendo) {
      console.info(
        "[enterprise-product-experience-support-menu] pendo trigger",
      );
      return;
    }
    this._panelOpen = !this._panelOpen;
  }

  private _handleClosePanel(): void {
    this._panelOpen = false;
    this._triggerEl?.focus();
  }

  override render() {
    return html`
      <support-menu-trigger
        .open=${this._panelOpen}
        .pendoMode=${this.showPendo}
        .dark=${this._isDarkTheme()}
        .label=${this._resolvedLabel}
        .locale=${this._resolvedLocale}
        @support-menu-trigger-toggle=${this._handleTriggerToggle}
      ></support-menu-trigger>
    `;
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, SupportMenuElement);
}

declare global {
  interface HTMLElementTagNameMap {
    "enterprise-product-experience-support-menu": SupportMenuElement;
  }
}
